"use client";

import { useEffect } from "react";
import { useLocale, useTranslations } from "next-intl";
import { CheckCircle, AlertTriangle, X, Shield } from "lucide-react";
import { CustomTooltip, Loading, Progress } from "./";
import { useAuth } from "@/app/hooks";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { useAiSourceSelector } from "@/app/store/ai-source-selector";

interface UsageIndicatorProps {
   className?: string;
   showDetails?: boolean;
   compact?: boolean;
   iconOnly?: boolean;
}

export const computeRemainingPercentage = (
   remainingRequests: number,
   maxRequestLimit: number
): number => {
   if (maxRequestLimit <= 0 || remainingRequests >= maxRequestLimit) return 100;
   if (remainingRequests <= 0) return 0;
   return Math.max(1, Math.floor((remainingRequests / maxRequestLimit) * 100));
};

const getCurrentMonthName = (locale: string): string => {
   const now = new Date();
   return now.toLocaleDateString(locale === "vi" ? "vi-VN" : "en-US", {
      month: "long"
   });
};

export const UsageIndicator = ({
   className = "",
   showDetails = false,
   compact = false,
   iconOnly = false
}: UsageIndicatorProps) => {
   const translate = useTranslations("Usage");
   const locale = useLocale();
   const { user } = useAuth();
   const { selectedBaseKnowledge } = useAiSourceSelector();
   const {
      usageData,
      loadingUsage: loading,
      errorUsage: error,
      fetchUsageData,
      shouldShowLimitForProject
   } = useStateHistoryChatPage();

   const projectId = selectedBaseKnowledge.id;

   // Auto-fetch usage data when project changes or on initial load
   useEffect(() => {
      if (user?.id && !user?.isAdmin && projectId) {
         fetchUsageData(projectId);
      }
   }, [user?.id, user?.isAdmin, projectId, fetchUsageData]);

   const showLimit = shouldShowLimitForProject(projectId);

   const renderCompactUsage = (
      remainingPercent: number,
      strokeColor: string,
      tooltipText: string
   ) => {
      const circumference = 2 * Math.PI * 7.5;

      return (
         <CustomTooltip text={tooltipText} delayDuration={300}>
            <div className={`flex items-center gap-1 ${className}`}>
               {!iconOnly && (
                  <span className="text-xs font-semibold tabular-nums text-foreground">
                     {remainingPercent}%
                  </span>
               )}
               <svg
                  width={18}
                  height={18}
                  viewBox="0 0 20 20"
                  className="!h-[18px] !w-[18px] -rotate-90 shrink-0"
                  aria-hidden="true"
               >
                  <circle
                     cx="10"
                     cy="10"
                     r="7.5"
                     fill="none"
                     strokeWidth="2.5"
                     className="stroke-muted-foreground/25"
                  />
                  <circle
                     cx="10"
                     cy="10"
                     r="7.5"
                     fill="none"
                     strokeWidth="2.5"
                     strokeLinecap="round"
                     strokeDasharray={circumference}
                     strokeDashoffset={circumference * (1 - remainingPercent / 100)}
                     className={`transition-all duration-300 ${strokeColor}`}
                  />
               </svg>
            </div>
         </CustomTooltip>
      );
   };

   // Show unlimited-style UI for admin OR when limit display is hidden
   if (user?.isAdmin || !showLimit) {
      if (compact) {
         return (
            <CustomTooltip
               text={`100% • ${translate("adminUnlimitedUsage")}`}
               delayDuration={300}
            >
               <div className={`flex items-center gap-1 ${className}`}>
                  {!iconOnly && (
                     <span className="text-xs font-semibold tabular-nums text-primary">
                        ∞
                     </span>
                  )}
                  <Shield size={16} className="text-primary shrink-0" />
               </div>
            </CustomTooltip>
         );
      }

      return (
         <div className={`flex items-center gap-2 ${className}`}>
            <Shield size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
               {translate("adminUnlimitedUsage")}
            </span>
         </div>
      );
   }

   const getUsageStatus = (
      percentage: number
   ): {
      color: "destructive" | "warning" | "success" | "default";
      icon: typeof CheckCircle;
   } => {
      if (percentage >= 90) return { color: "destructive", icon: AlertTriangle };
      if (percentage >= 70) return { color: "warning", icon: AlertTriangle };
      return { color: "success", icon: CheckCircle };
   };

   const formatResetDate = (dateString: string) => {
      try {
         const date = new Date(dateString);
         return date.toLocaleDateString("vi-VN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric"
         });
      } catch {
         return dateString;
      }
   };

   if (loading || !usageData) {
      return <Loading size="sm" />;
   }

   if (error) {
      return (
         <div className={`text-destructive text-sm ${className}`}>
            <X size={14} className="inline mr-1" />
            {error || translate("errorLoading")}
         </div>
      );
   }

   const status = getUsageStatus(usageData.usagePercentage);
   const StatusIcon = status.icon;
   const currentMonth = getCurrentMonthName(locale);

   if (compact) {
      const remainingPercent = computeRemainingPercentage(
         usageData.remainingRequests ?? 0,
         usageData.maxRequestLimit ?? 0
      );
      const strokeColor =
         status.color === "destructive"
            ? "stroke-destructive"
            : status.color === "warning"
              ? "stroke-yellow-500"
              : "stroke-primary";
      const tooltipText = `${remainingPercent}% • ${usageData.remainingRequests}/${usageData.maxRequestLimit} ${translate("questions")} (${currentMonth}) • ${translate("resetOn")}: ${formatResetDate(usageData.resetDate)}`;

      return renderCompactUsage(remainingPercent, strokeColor, tooltipText);
   }

   // Full mode (not compact)
   return (
      <div className={`space-y-2 ${className}`}>
         {/* Main Usage Display */}
         <div className="flex items-center gap-2">
            <StatusIcon
               size={16}
               className={`${
                  status.color === "destructive"
                     ? "text-red-500"
                     : status.color === "warning"
                       ? "text-yellow-500"
                       : "text-green-500"
               }`}
            />
            <div className="flex-1">
               <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-1">
                     <span className="text-sm font-bold">
                        {usageData.remainingRequests}/{usageData.maxRequestLimit}
                     </span>
                     <span className="text-xs font-medium text-muted-foreground">
                        {translate("questions")}
                     </span>
                  </div>
                  <span className="bg-secondary/60 text-secondary-foreground text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                     {currentMonth}
                  </span>
               </div>
               <Progress
                  value={usageData.usagePercentage}
                  className="h-2 mt-1"
                  color={status.color}
               />
            </div>
         </div>

         {/* Reset Date */}
         <div className="text-xs text-muted-foreground">
            {translate("resetOn")}: {formatResetDate(usageData.resetDate)}
         </div>

         {/* Detailed Information */}
         {showDetails && usageData.dailyBreakdown && (
            <div className="mt-3 p-2 bg-muted/30 rounded-md">
               <div className="text-xs font-medium mb-2">{translate("dailyUsage")}</div>
               <div className="space-y-1">
                  {usageData.dailyBreakdown.slice(-7).map((day) => (
                     <div key={day.date} className="flex justify-between text-xs">
                        <span>{day.date}</span>
                        <span>
                           {day.count} {translate("requests")}
                        </span>
                     </div>
                  ))}
               </div>
            </div>
         )}

         {/* Warning Message */}
         {usageData.usagePercentage >= 90 && (
            <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950/20 p-2 rounded">
               {translate("warningMessage")}
            </div>
         )}
      </div>
   );
};
